import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

// epg (air)time helper
// This mixin provides methods to handle airtime options and format dates for TV movie applications.
// It includes methods to convert airtime options into JavaScript Date objects and format them for use in HTML input elements.
// The `airTimeNow` computed property returns the current time rounded to the next 5 minutes in ISO format.
// The `getAirTimeByAirTimeOption` method converts a given airtime option into a JavaScript Date object.
// The `formatLocalDateTime` method formats a JavaScript Date object into a `datetime-local` compatible string.
export default {
  data () {
    return {
      timeZone: 'Europe/Berlin' // our default timezone
    }
  },
  computed: {
    // Returns the current time rounded to the next 5 minutes in ISO format
    airTimeNow () {
      // get current time from our timezone - important for correct display even if the user/google is in a different timezone
      const now = utcToZonedTime(new Date(), this.timeZone)
      now.setMinutes(Math.ceil(now.getMinutes() / 5) * 5, 0, 0) // round to next 5 minutes
      return now.toISOString()
    },
    browserTimeZone () {
      return Intl.DateTimeFormat().resolvedOptions().timeZone
    }
  },
  methods: {
    /**
     * Converts a given airtime option into a JavaScript Date object.
     * @param {string} airTimeOption - The airtime option to convert.
     * @returns {string} The converted airtime in ISO format.
     */
    getAirTimeByAirTimeOption (airTimeOption) {
      if (!airTimeOption) return null

      // get current time from our timezone - important for correct display even if the user/google is in a different timezone
      const now = utcToZonedTime(new Date(), this.timeZone)
      now.setSeconds(0, 0) // set seconds & milliseconds to 0

      // Split option into date part and optional time part
      const [datePart, timePart] = airTimeOption.split(',')

      // Helper function to shift the current date to a specific day by adding days
      const shiftNowByDays = (days) => {
        now.setDate(now.getDate() + days)
      }

      // Helper function to apply a time string (HH:MM) to a date object
      const applyTimeToDate = (date, time) => {
        if (time) {
          const [hours, minutes] = time.split(':').map(Number)
          date.setHours(hours, minutes)
        }
      }

      const convertToUtcIsoString = (date) => {
        return zonedTimeToUtc(date, this.timeZone).toISOString()
      }

      switch (datePart) {
        case 'now':
          return this.airTimeNow

        case 'today':
          applyTimeToDate(now, timePart)
          return convertToUtcIsoString(now)

        case 'tomorrow':
          // Shift the current date to tomorrow
          shiftNowByDays(1)
          applyTimeToDate(now, timePart)
          return convertToUtcIsoString(now)

        case 'friday':
        case 'saturday':
        case 'sunday': {
          // Map day names to their corresponding day numbers (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
          // This allows us to calculate the next occurrence of the specified day
          const dayMap = { friday: 5, saturday: 6, sunday: 0 }
          // Get the target day number from the map with the provided datePart
          const targetDay = dayMap[datePart]
          // Calculate days to add to get to the target day
          // Add 7 to ensure we always get the next occurrence of the target day
          const daysToAdd = (targetDay - now.getDay() + 7) % 7
          // Set the date to the next occurrence of the target day by adding calculated daysToAdd
          shiftNowByDays(daysToAdd)
          applyTimeToDate(now, timePart)
          return convertToUtcIsoString(now)
        }

        default:
          return this.selectedFilters.airTime // custom does not need any changes
      }
    },

    /**
         * Formats a JavaScript Date object into a `datetime-local` compatible string.
         *
         * @param {Date} date - The JavaScript Date object to format.
         * @returns {string} A string formatted as `YYYY-MM-DDTHH:MM`, compatible with HTML <input type="datetime-local">.
         *
         * Example:
         * ```js
         * const date = new Date('2025-04-02T14:30:00Z')
         * console.log(formatLocalDateTime(date)) // "2025-04-02T16:30" (if UTC+2)
         * ```
         */
    formatLocalDateTime (date) {
      // ensure correct type
      if (typeof date === 'string') {
        date = new Date(date)
      }
      // Ensures that numbers (month, day, hour, minute) are always two digits (e.g., "01" instead of "1").
      const pad = (num) => num.toString().padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    forceDateIntoTimeZone (date) {
      const utc = zonedTimeToUtc(date, this.browserTimeZone) // 1. interpret original date as "local" in fromTimeZone
      return utcToZonedTime(utc, this.timeZone) // 2. reinterpret it in the target time zone and return it
    }
  }
}
